import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import Profile from "../../assets/images/couple-default.png";
import AuthApi from "../../api/authApi";
import Helpers from "../../components/Helpers";
import lovestrongLogo from "../../assets/images/Lovestrong-logo.jpg";
import corruptedImg from "../../assets/images/corrupted-img.png";
import Loader from "../../components/loader";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import priestProfile from "../../assets/images/priest-default.png";
import ImagePlaceHolder from "../../assets/images/image-placeholder.png";

const Directory = () => {
  let authApi = new AuthApi();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const autoDownloadStarted = useRef(false);
  const [directory, setDirectory] = useState([]);
  const [data, setData] = useState([]);
  const [allRetreatRoasters, setAllRetreatRoasters] = useState([]);
  const [loading, setLoading] = useState(true); // ✅ loader state

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // show loader before fetching
      await Promise.all([
        getDirectoryDetail(id),
        getRetreatDetail(id),
        getAllRoastersOfRetreat(id),
      ]);
      setLoading(false); // hide loader after all done
    };
    fetchData();
  }, [id]);

  const getRetreatDetail = async (id) => {
    try {
      const res = await authApi.getRetreatDetail({ id: id });
      if (res && res.data && res.data.length > 0) {
        const { schedule, ...rest } = res.data[0];
        const parsedSchedule = schedule ? JSON.parse(schedule) : [];
        const parsedData = {
          ...rest,
          schedule: parsedSchedule.map((entry) => ({
            date: new Date(entry.date),
            from: entry.from,
            to: entry.to,
          })),
          parish: res.data[0].parishId,
        };
        setData(parsedData);
      } else {
        setData([]);
      }
    } catch (error) {
      toast.error(error.toString());
    }
  };

  const getDirectoryDetail = async (id) => {
    try {
      const response = await authApi.getDirectoryDetail({ rereatId: id });
      if (response?.data?.length > 0) {
        let directory = response.data[0];
        if (directory?.socialLinks) {
          directory.socialLinks =
            typeof directory.socialLinks === "string"
              ? JSON.parse(directory.socialLinks)
              : directory.socialLinks;
        } else {
          directory.socialLinks = {};
        }
        // Parse footer images if it's a JSON string
        if (directory && directory.footerImage) {
          try {
            const parsed = JSON.parse(directory.footerImage);
            if (Array.isArray(parsed)) {
              directory.footerImages = parsed;
            } else {
              // Old format - single image, convert to array
              directory.footerImages = [directory.footerImage];
            }
          } catch (e) {
            // If not JSON, treat as single image
            directory.footerImages = [directory.footerImage];
          }
        } else {
          directory.footerImages = [];
        }
        setDirectory(directory);
      } else {
        setDirectory({});
      }
    } catch (error) {
      toast.error(error.toString());
    }
  };

  const getAllRoastersOfRetreat = async (id) => {
    try {
      const response = await authApi.getAllRoastersOfRetreat({ rereatId: id });
      if (response?.data) {
        setAllRetreatRoasters(response.data);
      } else {
        setAllRetreatRoasters([]);
      }
    } catch (error) {
      toast.error(error.toString());
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const element = document.getElementById("directory-pdf-content");

      if (!element) {
        toast.error("Content not found for PDF generation");
        return;
      }

      toast.info("File is Downloading, please wait...");

      const clonedElement = element.cloneNode(true);

      const convertImageToDataURL = (img) => {
        return new Promise((resolve) => {
          if (img.src.startsWith("data:")) {
            resolve(img.src);
            return;
          }

          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const tempImg = new Image();

          tempImg.crossOrigin = "anonymous";

          tempImg.onload = () => {
            try {
              canvas.width = tempImg.naturalWidth || tempImg.width;
              canvas.height = tempImg.naturalHeight || tempImg.height;
              ctx.fillStyle = "#FFFFFF";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(tempImg, 0, 0);
              const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
              resolve(dataUrl);
            } catch (error) {
              console.warn("Failed to convert image:", img.src, error);
              resolve(img.src);
            }
          };

          tempImg.onerror = () => {
            console.warn("Failed to load image:", img.src);
            resolve(img.src);
          };

          const separator = img.src.includes("?") ? "&" : "?";
          tempImg.src = img.src + separator + "cb=" + Date.now();
          setTimeout(() => resolve(img.src), 5000);
        });
      };

      const images = clonedElement.getElementsByTagName("img");
      const imageArray = Array.from(images);

      const batchSize = 5;
      for (let i = 0; i < imageArray.length; i += batchSize) {
        const batch = imageArray.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (img) => {
            try {
              const styleAttr = img.getAttribute("style") || "";
              const originalHeight =
                img.style.height ||
                (styleAttr.match(/height:\s*([^;]+)/i)
                  ? styleAttr.match(/height:\s*([^;]+)/i)[1].trim()
                  : null);

              const dataUrl = await convertImageToDataURL(img);
              img.src = dataUrl;
              img.loading = "eager";
              img.style.maxWidth = "100%";
              img.style.opacity = "1"; // Force show for PDF

              if (originalHeight && originalHeight !== "auto") {
                img.style.height = originalHeight;
              } else {
                img.style.height = "auto";
              }

              img.style.display = "block";

              const isInCoupleImages = img.closest(".coupleImages");
              if (!isInCoupleImages) {
                img.style.margin = "0 auto";
              } else {
                img.style.margin = "0";
              }

              img.style.pageBreakInside = "avoid";
              img.style.breakInside = "avoid";
              img.style.pageBreakAfter = "avoid";

              const parent = img.parentElement;
              if (parent) {
                parent.style.pageBreakInside = "avoid";
                parent.style.breakInside = "avoid";
              }
            } catch (error) {
              console.warn("Error processing image:", error);
            }
          }),
        );
      }

      clonedElement.style.position = "absolute";
      clonedElement.style.left = "-9999px";
      clonedElement.style.top = "0";
      clonedElement.style.width = "1200px";
      clonedElement.style.backgroundColor = "#ffffff";
      clonedElement.style.padding = "40px";
      clonedElement.style.boxSizing = "border-box";
      clonedElement.style.fontFamily = "Arial, sans-serif";

      const rows = clonedElement.querySelectorAll(".mt-5, .my-5, .mt-3");
      rows.forEach((row) => {
        row.style.pageBreakInside = "avoid";
        row.style.breakInside = "avoid";
        row.style.marginBottom = "40px";
        row.style.paddingBottom = "20px";
      });

      // ✅ Cap main header image — full width, same as content divs
      const mainHeaderImg = clonedElement.querySelector(
        ".directoryImgMain img",
      );
      if (mainHeaderImg) {
        mainHeaderImg.style.maxHeight = "1000px";
        mainHeaderImg.style.width = "100%";
        mainHeaderImg.style.objectFit = "cover";
        mainHeaderImg.style.display = "block";
        mainHeaderImg.style.margin = "0 auto";
      }
      const mainHeaderContainer =
        clonedElement.querySelector(".directoryImgMain");
      if (mainHeaderContainer) {
        mainHeaderContainer.style.width = "100%";
        mainHeaderContainer.style.padding = "0";
        mainHeaderContainer.style.margin = "0";
      }

      // ✅ Cap footer images — full width, same as content divs
      const footerImageContainers =
        clonedElement.querySelectorAll(".footer_images");
      footerImageContainers.forEach((footerImgWrapper) => {
        footerImgWrapper.style.pageBreakInside = "avoid";
        footerImgWrapper.style.breakInside = "avoid";
        footerImgWrapper.style.marginBottom = "30px";
        footerImgWrapper.style.paddingBottom = "20px";
        footerImgWrapper.style.width = "100%";
        footerImgWrapper.style.padding = "0";
        footerImgWrapper.setAttribute("data-force-page-break", "true");

        const footerImg = footerImgWrapper.querySelector("img");
        if (footerImg) {
          footerImg.style.maxHeight = "1000px";
          footerImg.style.width = "100%";
          footerImg.style.objectFit = "cover";
          footerImg.style.display = "block";
          footerImg.style.margin = "0 auto";
        }
      });

      const leaderShipSections = clonedElement.querySelectorAll(".leaderShip");
      leaderShipSections.forEach((section) => {
        section.style.pageBreakInside = "avoid";
        section.style.breakInside = "avoid";
        section.style.paddingBottom = "30px";
        section.style.paddingTop = "0";
        section.style.marginTop = "0";

        const allImgs = section.querySelectorAll("img");
        allImgs.forEach((img) => {
          const logoContainer = img.parentElement;
          if (logoContainer) {
            logoContainer.style.pageBreakInside = "avoid";
            logoContainer.style.breakInside = "avoid";
            logoContainer.style.pageBreakAfter = "avoid";
            logoContainer.style.marginBottom = "20px";
          }
        });

        const sectionHeadings = section.querySelectorAll(
          "h2.LeadHeading, .LeadHeading",
        );
        sectionHeadings.forEach((heading) => {
          heading.style.pageBreakAfter = "avoid";
          heading.style.marginTop = "20px";
          heading.style.paddingTop = "10px";
          const nextElement = heading.nextElementSibling;
          if (nextElement) {
            nextElement.style.pageBreakBefore = "avoid";
          }
        });
      });

      const logoImages = clonedElement.querySelectorAll(".logo_image");
      logoImages.forEach((logo) => {
        const parent = logo.parentElement;
        if (parent) {
          parent.style.pageBreakInside = "avoid";
          parent.style.breakInside = "avoid";
          parent.style.paddingTop = "20px";
          parent.style.paddingBottom = "30px";
        }
      });

      const coupleRows = clonedElement.querySelectorAll(".row.mt-5, .row.my-5");
      coupleRows.forEach((row) => {
        const coupleImagesCol = row.querySelector(".coupleImages");
        const couplDetailCol = row.querySelector(".coupl_detail");
        if (coupleImagesCol) coupleImagesCol.style.paddingRight = "10px";
        if (couplDetailCol) couplDetailCol.style.paddingLeft = "10px";
      });

      const allElements = clonedElement.querySelectorAll("*");
      allElements.forEach((el) => {
        const isInCouplDetail = el.closest(".coupl_detail");
        const isInCoupleImages = el.closest(".coupleImages");

        if (el.tagName === "IMG" && !isInCoupleImages) {
          const parent = el.parentElement;
          if (parent) {
            parent.style.textAlign = "center";
            parent.style.display = "block";
            parent.style.width = "100%";
          }
        }

        if (
          !isInCouplDetail &&
          (el.tagName === "H1" ||
            el.tagName === "H2" ||
            el.tagName === "H3" ||
            el.tagName === "H4" ||
            el.tagName === "H5" ||
            el.tagName === "H6" ||
            el.classList.contains("text-center") ||
            el.style.textAlign === "center")
        ) {
          el.style.textAlign = "center";
          el.style.width = "100%";
        }

        if (isInCouplDetail) {
          el.style.textAlign = "left";
        }
      });

      const couplDetailCols = clonedElement.querySelectorAll(".coupl_detail");
      couplDetailCols.forEach((col) => {
        col.style.textAlign = "left";
        const allChildren = col.querySelectorAll("*");
        allChildren.forEach((child) => {
          if (
            !child.classList.contains("text-center") &&
            child.style.textAlign !== "center"
          ) {
            child.style.textAlign = "left";
          }
        });
      });

      document.body.appendChild(clonedElement);

      await new Promise((resolve) => setTimeout(resolve, 1000));

      const canvasScale = 1.5;

      let forcedBreakPositions = [];
      try {
        const parentRect = clonedElement.getBoundingClientRect();
        const forcedSections = clonedElement.querySelectorAll(
          "[data-force-page-break='true']",
        );
        forcedBreakPositions = Array.from(forcedSections).map((section) => {
          const rect = section.getBoundingClientRect();
          return (rect.top - parentRect.top) * canvasScale;
        });
      } catch (e) {
        console.warn("Failed to calculate forced break positions", e);
        forcedBreakPositions = [];
      }

      let leaderShipBreakPositions = [];
      try {
        const parentRect = clonedElement.getBoundingClientRect();
        const sections = clonedElement.querySelectorAll(".leaderShip");
        leaderShipBreakPositions = Array.from(sections).map((section) => {
          const rect = section.getBoundingClientRect();
          return (rect.top - parentRect.top) * canvasScale;
        });
      } catch (e) {
        console.warn("Failed to calculate leaderShip positions", e);
        leaderShipBreakPositions = [];
      }

      let coupleBlocks = [];
      try {
        const coupleRowsForBlocks = clonedElement.querySelectorAll(
          ".row.mt-5, .row.my-5",
        );
        const parentRect = clonedElement.getBoundingClientRect();
        coupleBlocks = Array.from(coupleRowsForBlocks)
          .filter((row) => row.querySelector(".coupleImages"))
          .map((row) => {
            const rect = row.getBoundingClientRect();
            const top = (rect.top - parentRect.top) * canvasScale;
            const bottom = (rect.bottom - parentRect.top) * canvasScale;
            return { top, bottom };
          });
      } catch (e) {
        console.warn("Failed to calculate couple block positions", e);
        coupleBlocks = [];
      }

      // ✅ Protect footer image blocks from being cut
      try {
        const parentRect = clonedElement.getBoundingClientRect();
        const footerImgBlocks =
          clonedElement.querySelectorAll(".footer_images");
        Array.from(footerImgBlocks).forEach((block) => {
          const rect = block.getBoundingClientRect();
          const top = (rect.top - parentRect.top) * canvasScale;
          const bottom = (rect.bottom - parentRect.top) * canvasScale;
          coupleBlocks.push({ top, bottom });
        });
      } catch (e) {
        console.warn("Failed to calculate footer image block positions", e);
      }

      const canvas = await html2canvas(clonedElement, {
        scale: canvasScale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 15000,
        windowWidth: 1200,
        scrollX: 0,
        scrollY: 0,
      });

      document.body.removeChild(clonedElement);

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const topPadding = 10;
      const bottomPadding = 20;
      const usablePdfHeight = pdfHeight - topPadding - bottomPadding;
      const pageHeightPixels = (usablePdfHeight * imgWidth) / pdfWidth;

      const isAreaMostlyWhite = (ctx, x, y, width, height) => {
        try {
          const imageData = ctx.getImageData(x, y, width, height);
          const pixels = imageData.data;
          let whitePixels = 0;
          const totalPixels = pixels.length / 4;
          for (let i = 0; i < pixels.length; i += 4) {
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            if (r > 240 && g > 240 && b > 240) whitePixels++;
          }
          return whitePixels / totalPixels > 0.95;
        } catch (error) {
          return false;
        }
      };

      const getSmartForcedBreakBetween = (startY, endY, currentPageStart) => {
        for (const breakPos of forcedBreakPositions) {
          if (breakPos > startY + 20 && breakPos < endY) {
            return breakPos;
          }
        }
        const topThreshold = currentPageStart + pageHeightPixels * 0.15;
        for (const breakPos of leaderShipBreakPositions) {
          if (
            breakPos > startY + 20 &&
            breakPos < endY &&
            breakPos > topThreshold
          ) {
            return breakPos;
          }
        }
        return null;
      };

      const findBestBreakPoint = (
        ctx,
        startY,
        idealBreakY,
        searchRange = 100,
      ) => {
        const scanHeight = 5;
        let bestBreakY = idealBreakY;
        let maxWhiteSpace = 0;

        for (
          let y = idealBreakY - searchRange;
          y <= idealBreakY + searchRange;
          y += scanHeight
        ) {
          if (y < startY || y >= imgHeight) continue;
          const isWhite = isAreaMostlyWhite(ctx, 0, y, imgWidth, scanHeight);
          if (isWhite) {
            const distanceFromIdeal = Math.abs(y - idealBreakY);
            const score = 1 / (distanceFromIdeal + 1);
            if (score > maxWhiteSpace) {
              maxWhiteSpace = score;
              bestBreakY = y;
            }
          }
        }

        if (maxWhiteSpace === 0) bestBreakY = idealBreakY;

        if (coupleBlocks && coupleBlocks.length) {
          const margin = 10;
          coupleBlocks.forEach((block) => {
            if (
              bestBreakY > block.top + margin &&
              bestBreakY < block.bottom - margin
            ) {
              const moveUp = Math.max(startY, block.top - margin);
              const moveDown = Math.min(imgHeight, block.bottom + margin);
              const distUp = Math.abs(moveUp - idealBreakY);
              const distDown = Math.abs(moveDown - idealBreakY);
              bestBreakY = distUp <= distDown ? moveUp : moveDown;
            }
          });
        }

        return bestBreakY;
      };

      const ctx = canvas.getContext("2d");
      const pages = [];

      let tempY = 0;
      while (tempY < imgHeight) {
        const remainingHeight = imgHeight - tempY;
        let pageContentHeight;

        if (remainingHeight <= pageHeightPixels) {
          pageContentHeight = remainingHeight;
        } else {
          const idealBreakY = tempY + pageHeightPixels;
          const forcedBreak = getSmartForcedBreakBetween(
            tempY,
            idealBreakY,
            tempY,
          );

          if (forcedBreak !== null) {
            pageContentHeight = forcedBreak - tempY;
            if (pageContentHeight < pageHeightPixels * 0.15) {
              const searchRange = Math.min(150, pageHeightPixels * 0.3);
              const bestBreakY = findBestBreakPoint(
                ctx,
                tempY,
                idealBreakY,
                searchRange,
              );
              pageContentHeight = bestBreakY - tempY;
              if (pageContentHeight < pageHeightPixels * 0.5) {
                pageContentHeight = Math.min(pageHeightPixels, remainingHeight);
              }
            }
          } else {
            const searchRange = Math.min(150, pageHeightPixels * 0.3);
            const bestBreakY = findBestBreakPoint(
              ctx,
              tempY,
              idealBreakY,
              searchRange,
            );
            pageContentHeight = bestBreakY - tempY;
            if (pageContentHeight < pageHeightPixels * 0.5) {
              pageContentHeight = Math.min(pageHeightPixels, remainingHeight);
            }
          }
        }

        pages.push({ startY: tempY, height: pageContentHeight });
        tempY += pageContentHeight;
        if (pages.length > 100) break;
      }

      const totalPages = pages.length;

      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage();

        const page = pages[i];
        const pageContentHeight = page.height;

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = imgWidth;
        pageCanvas.height = pageContentHeight;
        const pageCtx = pageCanvas.getContext("2d");

        pageCtx.fillStyle = "#FFFFFF";
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageCtx.drawImage(
          canvas,
          0,
          page.startY,
          imgWidth,
          pageContentHeight,
          0,
          0,
          imgWidth,
          pageContentHeight,
        );

        const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.85);
        const renderWidth = pdfWidth;
        const renderHeight = (pageContentHeight * pdfWidth) / imgWidth;

        pdf.addImage(
          pageImgData,
          "JPEG",
          0,
          topPadding,
          renderWidth,
          renderHeight,
        );

        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        const pageText = `${i + 1} / ${totalPages}`;
        const textWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, (pdfWidth - textWidth) / 2, pdfHeight - 7);

        pageCanvas.width = 0;
        pageCanvas.height = 0;
      }

      canvas.width = 0;
      canvas.height = 0;

      const fileName = `directory-${
        data && data.title ? data.title.replace(/[^a-z0-9]/gi, "_") : "retreat"
      }-${moment().format("YYYY-MM-DD")}.pdf`;

      pdf.save(fileName);
      toast.success(`File is Downloaded.`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  useEffect(() => {
    if (searchParams.get("download") !== "1") return;
    if (loading || !data?.id || autoDownloadStarted.current) return;
    autoDownloadStarted.current = true;
    handleDownloadPDF();
  }, [loading, data?.id, searchParams]);

  return (
    <div className="container-scroller mainpannel avoid-break">
      <div className="container-fluid page-body-wrapper full-page-wrapper">
        <div className="content-wrapper align-items-center auth px-0">
          <div className="row w-100 mx-0">
            <div className="col-lg-10 mx-auto">
              {loading ? (
                <div className="directory-preview-loader">
                  <Loader />
                </div>
              ) : (
                <>
              {data && data.id && (
                <div className="d-flex justify-content-center my-2">
                  <Button
                    onClick={handleDownloadPDF}
                    size="lg"
                    className="px-2 py-2 fw-bold shadow-lg download-btn"
                  >
                    Download PDF
                  </Button>
                </div>
              )}
              <div className="auth-form-light main-register py-5 px-4 px-sm-5">
                <div id="directory-pdf-content">
                  {data && data.id ? (
                    <Row>
                      <Col className="text-center">
                        {" "}
                        <img
                          src={lovestrongLogo}
                          alt=""
                          srcset=""
                          style={{
                            height: "140px",
                            marginTop: "15px",
                          }}
                        />
                      </Col>
                      {data && data.heading ? (
                        <Col
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          className="directoryHeader howerOption"
                        >
                          <div>
                            <h1>
                              <em>
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: data && data.heading,
                                  }}
                                />{" "}
                              </em>
                            </h1>
                          </div>
                        </Col>
                      ) : null}

                      {data && data.image1 ? (
                        <Col
                          className="directoryImgMain howerOption p-0"
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                        >
                          <div className="text-center">
                            <img
                              src={data && data.image1}
                              className="imageHeader"
                            />
                          </div>
                        </Col>
                      ) : null}
                      <Col
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        className="directoryHeaderInMain"
                      >
                        <div>
                          <p>Retreat Directory</p>{" "}
                          <h2> {data?.myparish?.parish}</h2>
                          <h1 className="mt-4">
                            {data.dateFrom && data.dateTo ? (
                              <span>
                                {moment(data.dateFrom).isSame(
                                  data.dateTo,
                                  "day",
                                )
                                  ? // Same day
                                    `${moment(data.dateFrom)
                                      .tz("America/New_York")
                                      .format("MMM D, YYYY")}`
                                  : moment(data.dateFrom).isSame(
                                        data.dateTo,
                                        "month",
                                      )
                                    ? // Same month, show month only once
                                      `${moment(data.dateFrom)
                                        .tz("America/New_York")
                                        .format("MMM D")} - ${moment(
                                        data.dateTo,
                                      )
                                        .tz("America/New_York")
                                        .format("D")}, ${moment(data.dateFrom)
                                        .tz("America/New_York")
                                        .format("YYYY")}`
                                    : // Different months
                                      `${moment(data.dateFrom)
                                        .tz("America/New_York")
                                        .format("MMM D")} - ${moment(
                                        data.dateTo,
                                      )
                                        .tz("America/New_York")
                                        .format("MMM D")}, ${moment(
                                        data.dateFrom,
                                      )
                                        .tz("America/New_York")
                                        .format("YYYY")}`}
                              </span>
                            ) : null}
                          </h1>
                        </div>
                      </Col>
                      {(allRetreatRoasters &&
                        allRetreatRoasters[0] &&
                        allRetreatRoasters[0].lead &&
                        allRetreatRoasters[0].lead.length) ||
                      (allRetreatRoasters &&
                        allRetreatRoasters[1] &&
                        allRetreatRoasters[1].coLead &&
                        allRetreatRoasters[1].coLead.length) ||
                      (allRetreatRoasters &&
                        allRetreatRoasters[2] &&
                        allRetreatRoasters[2].mentor &&
                        allRetreatRoasters[2].mentor.length) ||
                      (allRetreatRoasters &&
                        allRetreatRoasters[4] &&
                        allRetreatRoasters[4].sprDirector &&
                        allRetreatRoasters[4].sprDirector.length) ||
                      (allRetreatRoasters &&
                        allRetreatRoasters[3] &&
                        allRetreatRoasters[3].sprAdvisor &&
                        allRetreatRoasters[3].sprAdvisor.length) ? (
                        <Col
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          className="colorTalks mt-3 p-5 leaderShip text-center"
                        >
                          <h2 className="LeadHeading">Leadership</h2>

                          {allRetreatRoasters &&
                            allRetreatRoasters[0] &&
                            allRetreatRoasters[0].lead &&
                            allRetreatRoasters[0].lead.map((item, i) => {
                              return (
                                <Row key={i} className="mt-5">
                                  <Col
                                    md={5}
                                    className="coupleImages coupleImageHower"
                                  >
                                    {(item && item.imageUrl == "") ||
                                    item.imageUrl == undefined ? (
                                      <div className="edit-profile-img mb-2  imageHeader">
                                        <img
                                          src={Profile}
                                          className="shadowImage"
                                        />
                                      </div>
                                    ) : (
                                      <div className="edit-profile-img mb-2  imageHeader">
                                        <img
                                          src={item && item.imageUrl}
                                          className="shadowImage profileImage"
                                          loading="lazy"
                                        />
                                      </div>
                                    )}{" "}
                                  </Col>
                                  <Col md={7} className="coupl_detail">
                                    {i === 0 ? <h4>Lead Couple</h4> : null}
                                    <h2 className="mt-3 mb-3">
                                      {`${
                                        item?.hisFirstName
                                          ? item.hisFirstName
                                          : ""
                                      }${
                                        item?.hisFirstName && item?.herFirstName
                                          ? " & "
                                          : ""
                                      }${
                                        item?.herFirstName
                                          ? item.herFirstName
                                          : ""
                                      } ${
                                        item?.hisLastName
                                          ? item.hisLastName
                                          : item?.herLastName
                                            ? item.herLastName
                                            : ""
                                      }`}
                                    </h2>
                                    <h4>
                                      {" "}
                                      {"His : " +
                                        `${
                                          item && item.hisMobile
                                            ? item.hisMobile
                                            : ""
                                        }`}
                                    </h4>
                                    <h4>{`${
                                      item && item.hisEmail ? item.hisEmail : ""
                                    }`}</h4>
                                    <h4>
                                      {" "}
                                      {"Hers : " +
                                        `${
                                          item && item.herMobile
                                            ? item.herMobile
                                            : ""
                                        }`}
                                    </h4>
                                    <h4>{`${
                                      item && item.herEmail ? item.herEmail : ""
                                    }`}</h4>{" "}
                                    {item && item.anniversary_date ? (
                                      <h4>
                                        {"Anniversary Date : " +
                                          `${
                                            item && item.anniversary_date
                                              ? Helpers.convertUtcToTimezone(
                                                  item.anniversary_date,
                                                  "MM/DD",
                                                )
                                              : ""
                                          }`}
                                      </h4>
                                    ) : (
                                      ""
                                    )}
                                  </Col>
                                </Row>
                              );
                            })}

                          {allRetreatRoasters &&
                            allRetreatRoasters[1] &&
                            allRetreatRoasters[1].coLead &&
                            allRetreatRoasters[1].coLead.map((item, i) => {
                              return (
                                <Row key={i} className="mt-5">
                                  <Col
                                    md={5}
                                    className="coupleImages coupleImageHower"
                                  >
                                    {(item && item.imageUrl == "") ||
                                    item.imageUrl == undefined ? (
                                      <div className="edit-profile-img mb-2  imageHeader">
                                        <img
                                          src={Profile}
                                          className="shadowImage"
                                        />
                                      </div>
                                    ) : (
                                      <div className="edit-profile-img mb-2  imageHeader">
                                        <img
                                          src={item && item.imageUrl}
                                          className="shadowImage profileImage"
                                          loading="lazy"
                                        />
                                      </div>
                                    )}{" "}
                                  </Col>
                                  <Col md={7} className="coupl_detail">
                                    {i === 0 ? <h4>Co-Lead Couple</h4> : null}
                                    <h2 className="mt-3 mb-3">
                                      {`${
                                        item?.hisFirstName
                                          ? item.hisFirstName
                                          : ""
                                      }${
                                        item?.hisFirstName && item?.herFirstName
                                          ? " & "
                                          : ""
                                      }${
                                        item?.herFirstName
                                          ? item.herFirstName
                                          : ""
                                      } ${
                                        item?.hisLastName
                                          ? item.hisLastName
                                          : item?.herLastName
                                            ? item.herLastName
                                            : ""
                                      }`}
                                    </h2>

                                    {(item?.hisMobile || item?.hisEmail) && (
                                      <>
                                        <h4>
                                          His:{" "}
                                          {item.hisMobile ? item.hisMobile : ""}
                                        </h4>
                                        {item.hisEmail && (
                                          <h4>{item.hisEmail}</h4>
                                        )}
                                      </>
                                    )}

                                    {(item?.herMobile || item?.herEmail) && (
                                      <>
                                        <h4>
                                          Hers:{" "}
                                          {item.herMobile ? item.herMobile : ""}
                                        </h4>
                                        {item.herEmail && (
                                          <h4>{item.herEmail}</h4>
                                        )}
                                      </>
                                    )}
                                    {item && item.anniversary_date ? (
                                      <h4>
                                        {"Anniversary Date : " +
                                          `${
                                            item && item.anniversary_date
                                              ? Helpers.convertUtcToTimezone(
                                                  item.anniversary_date,
                                                  "MM/DD",
                                                )
                                              : ""
                                          }`}
                                      </h4>
                                    ) : (
                                      ""
                                    )}
                                  </Col>
                                </Row>
                              );
                            })}

                          {allRetreatRoasters &&
                            allRetreatRoasters[2] &&
                            allRetreatRoasters[2].mentor &&
                            allRetreatRoasters[2].mentor.map((item, i) => {
                              return (
                                <Row key={i} className="my-5">
                                  <Col
                                    md={5}
                                    className="coupleImages coupleImageHower"
                                  >
                                    {(item && item.imageUrl == "") ||
                                    item.imageUrl == undefined ? (
                                      <div className="edit-profile-img mb-2  imageHeader">
                                        <img
                                          src={Profile}
                                          className="shadowImage"
                                        />
                                      </div>
                                    ) : (
                                      <div className="edit-profile-img mb-2  imageHeader">
                                        <img
                                          src={item && item.imageUrl}
                                          className="shadowImage profileImage"
                                          loading="lazy"
                                        />
                                      </div>
                                    )}{" "}
                                  </Col>
                                  <Col md={7} className="coupl_detail">
                                    {i === 0 ? <h4>Mentor Couple</h4> : null}
                                    <h2 className="mt-3 mb-3">
                                      {`${
                                        item?.hisFirstName
                                          ? item.hisFirstName
                                          : ""
                                      }${
                                        item?.hisFirstName && item?.herFirstName
                                          ? " & "
                                          : ""
                                      }${
                                        item?.herFirstName
                                          ? item.herFirstName
                                          : ""
                                      } ${
                                        item?.hisLastName
                                          ? item.hisLastName
                                          : item?.herLastName
                                            ? item.herLastName
                                            : ""
                                      }`}
                                    </h2>

                                    {(item?.hisMobile || item?.hisEmail) && (
                                      <>
                                        <h4>
                                          His:{" "}
                                          {item.hisMobile ? item.hisMobile : ""}
                                        </h4>
                                        {item.hisEmail && (
                                          <h4>{item.hisEmail}</h4>
                                        )}
                                      </>
                                    )}

                                    {(item?.herMobile || item?.herEmail) && (
                                      <>
                                        <h4>
                                          Hers:{" "}
                                          {item.herMobile ? item.herMobile : ""}
                                        </h4>
                                        {item.herEmail && (
                                          <h4>{item.herEmail}</h4>
                                        )}
                                      </>
                                    )}
                                    {item && item.anniversary_date ? (
                                      <h4>
                                        {"Anniversary Date : " +
                                          `${
                                            item && item.anniversary_date
                                              ? Helpers.convertUtcToTimezone(
                                                  item.anniversary_date,
                                                  "MM/DD",
                                                )
                                              : ""
                                          }`}
                                      </h4>
                                    ) : (
                                      ""
                                    )}
                                  </Col>
                                </Row>
                              );
                            })}
                          {allRetreatRoasters &&
                            allRetreatRoasters[3] &&
                            allRetreatRoasters[3].sprAdvisor &&
                            allRetreatRoasters[3].sprAdvisor.map((item, i) => {
                              return (
                                <Row key={i} className="my-5">
                                  <Col
                                    md={5}
                                    className="coupleImages coupleImageHower"
                                  >
                                    {(item && item.imageUrl == "") ||
                                    item.imageUrl == undefined ? (
                                      <div className="edit-profile-img mb-2  imageHeader">
                                        <img
                                          src={Profile}
                                          className="shadowImage"
                                        />
                                      </div>
                                    ) : (
                                      <div className="edit-profile-img mb-2  imageHeader">
                                        <img
                                          src={item && item.imageUrl}
                                          className="shadowImage profileImage"
                                          loading="lazy"
                                        />
                                      </div>
                                    )}{" "}
                                  </Col>
                                  <Col md={7} className="coupl_detail">
                                    {i === 0 ? <h4>Spiritual Mentor</h4> : null}

                                    <h2 className="mt-3 mb-3">
                                      {item?.hisFirstName && item?.herFirstName
                                        ? `${item.hisFirstName} & ${item.herFirstName} ${item.hisLastName || ""}`
                                        : item?.hisFirstName
                                          ? `${item.hisFirstName} ${item.hisLastName || ""}`
                                          : item?.herFirstName
                                            ? `${item.herFirstName} ${item.herLastName || ""}`
                                            : ""}
                                    </h2>

                                    {item?.hisFirstName &&
                                    item?.herFirstName ? (
                                      <>
                                        {(item?.hisMobile ||
                                          item?.hisEmail) && (
                                          <>
                                            <h4>His: {item.hisMobile || ""}</h4>
                                            {item.hisEmail && (
                                              <h4>{item.hisEmail}</h4>
                                            )}
                                          </>
                                        )}

                                        {(item?.herMobile ||
                                          item?.herEmail) && (
                                          <>
                                            <h4>
                                              Hers: {item.herMobile || ""}
                                            </h4>
                                            {item.herEmail && (
                                              <h4>{item.herEmail}</h4>
                                            )}
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        {(item?.hisMobile ||
                                          item?.hisEmail) && (
                                          <>
                                            <h4>{item.hisMobile || ""}</h4>
                                            {item.hisEmail && (
                                              <h4>{item.hisEmail}</h4>
                                            )}
                                          </>
                                        )}

                                        {(item?.herMobile ||
                                          item?.herEmail) && (
                                          <>
                                            <h4>{item.herMobile || ""}</h4>
                                            {item.herEmail && (
                                              <h4>{item.herEmail}</h4>
                                            )}
                                          </>
                                        )}
                                      </>
                                    )}

                                    {item?.anniversary_date && (
                                      <h4>
                                        {"Anniversary Date : " +
                                          Helpers.convertUtcToTimezone(
                                            item.anniversary_date,
                                            "MM/DD",
                                          )}
                                      </h4>
                                    )}
                                  </Col>
                                </Row>
                              );
                            })}
                          {allRetreatRoasters &&
                            allRetreatRoasters[4] &&
                            allRetreatRoasters[4].sprDirector &&
                            allRetreatRoasters[4].sprDirector.map((item, i) => {
                              return (
                                <Row key={i} className="my-5">
                                  <Col
                                    md={5}
                                    className="coupleImages coupleImageHower"
                                  >
                                    {(item && item.priestImage == "") ||
                                    item.priestImage == undefined ? (
                                      <div className="edit-profile-img mb-2  imageHeader">
                                        <img
                                          src={priestProfile}
                                          className="shadowImage profileImage"
                                        />
                                      </div>
                                    ) : (
                                      <div className="edit-profile-img mb-2  imageHeader">
                                        <img
                                          src={item && item.priestImage}
                                          className="shadowImage profileImage"
                                        />
                                      </div>
                                    )}{" "}
                                  </Col>
                                  <Col md={7} className="coupl_detail">
                                    {i === 0 && (
                                      <h4 className="mt-3">
                                        Spiritual Director
                                      </h4>
                                    )}
                                    <h2 className="mt-3 mb-3">
                                      {[
                                        item?.priestTitle,
                                        item?.priestFirstName,
                                        item?.priestLastName,
                                      ]
                                        .filter(Boolean)
                                        .join(" ")}
                                    </h2>
                                    {/* {item?.email && (
                                      <>
                                        <h4>
                                          Email: {item.email ? item.email : ""}
                                        </h4>
                                      </>
                                    )} */}
                                  </Col>
                                </Row>
                              );
                            })}
                        </Col>
                      ) : null}
                      {allRetreatRoasters &&
                      allRetreatRoasters[6] &&
                      allRetreatRoasters[6].retreatant.length &&
                      allRetreatRoasters[6].retreatant ? (
                        <Col
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          className="colorTalks mt-2 p-5 leaderShip text-center"
                        >
                          <img
                            src={lovestrongLogo}
                            alt=""
                            srcset=""
                            style={{
                              height: "90px",
                            }}
                          />
                          <h2 className="LeadHeading mt-4">Retreatants</h2>
                          {allRetreatRoasters &&
                            allRetreatRoasters[6] &&
                            allRetreatRoasters[6].retreatant &&
                            allRetreatRoasters[6].retreatant.map((item, i) => {
                              return (
                                <Row className="mt-5" key={i}>
                                  <Col
                                    md={5}
                                    className="coupleImages coupleImageHower"
                                  >
                                    {(item && item.imageUrl == "") ||
                                    item.imageUrl == undefined ? (
                                      <div className="edit-profile-img mb-2 imageHeader ">
                                        <img
                                          src={Profile}
                                          className="shadowImage"
                                        />
                                      </div>
                                    ) : (
                                      <div className="edit-profile-img mb-2  imageHeader ">
                                        <img
                                          src={item && item.imageUrl}
                                          className="shadowImage profileImage"
                                          loading="lazy"
                                        />
                                      </div>
                                    )}{" "}
                                  </Col>
                                  <Col md={7} className="coupl_detail">
                                    {" "}
                                    <h2 className="mt-3 mb-3">
                                      {`${
                                        item?.hisFirstName
                                          ? item.hisFirstName
                                          : ""
                                      }${
                                        item?.hisFirstName && item?.herFirstName
                                          ? " & "
                                          : ""
                                      }${
                                        item?.herFirstName
                                          ? item.herFirstName
                                          : ""
                                      } ${
                                        item?.hisLastName
                                          ? item.hisLastName
                                          : item?.herLastName
                                            ? item.herLastName
                                            : ""
                                      }`}
                                    </h2>
                                    {(item?.hisMobile || item?.hisEmail) && (
                                      <>
                                        <h4>
                                          His:{" "}
                                          {item.hisMobile ? item.hisMobile : ""}
                                        </h4>
                                        {item.hisEmail && (
                                          <h4>{item.hisEmail}</h4>
                                        )}
                                      </>
                                    )}
                                    {(item?.herMobile || item?.herEmail) && (
                                      <>
                                        <h4>
                                          Hers:{" "}
                                          {item.herMobile ? item.herMobile : ""}
                                        </h4>
                                        {item.herEmail && (
                                          <h4>{item.herEmail}</h4>
                                        )}
                                      </>
                                    )}
                                    {item && item.anniversary_date ? (
                                      <h4>
                                        {"Anniversary Date : " +
                                          `${
                                            item && item.anniversary_date
                                              ? Helpers.convertUtcToTimezone(
                                                  item.anniversary_date,
                                                  "MM/DD",
                                                )
                                              : ""
                                          }`}
                                      </h4>
                                    ) : (
                                      ""
                                    )}
                                  </Col>
                                </Row>
                              );
                            })}
                        </Col>
                      ) : null}
                      {allRetreatRoasters &&
                      allRetreatRoasters[5] &&
                      allRetreatRoasters[5].team &&
                      allRetreatRoasters[5].team.length ? (
                        <Col
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          className="colorTalks mt-3 p-5 leaderShip text-center"
                        >
                          {" "}
                          <img
                            src={lovestrongLogo}
                            alt="Lovestrong Logo"
                            style={{
                              height: "90px",
                            }}
                          />
                          <h2 className="LeadHeading mt-4">Team</h2>
                          {allRetreatRoasters &&
                            allRetreatRoasters[5] &&
                            allRetreatRoasters[5].team &&
                            allRetreatRoasters[5].team.map((item, i) => {
                              return (
                                <Row className="mt-5">
                                  <Col
                                    md={5}
                                    className="coupleImages coupleImageHower"
                                  >
                                    {(item && item.imageUrl == "") ||
                                    item.imageUrl == undefined ? (
                                      <div className="edit-profile-img mb-2 imageHeader">
                                        <img
                                          src={Profile}
                                          className="shadowImage"
                                        />
                                      </div>
                                    ) : (
                                      <div className="edit-profile-img mb-2 imageHeader">
                                        <img
                                          src={item && item.imageUrl}
                                          className="shadowImage profileImage"
                                          loading="lazy"
                                        />
                                      </div>
                                    )}{" "}
                                  </Col>
                                  <Col md={7} className="coupl_detail">
                                    {" "}
                                    <h2 className="mt-3 mb-3">
                                      {`${
                                        item?.hisFirstName
                                          ? item.hisFirstName
                                          : ""
                                      }${
                                        item?.hisFirstName && item?.herFirstName
                                          ? " & "
                                          : ""
                                      }${
                                        item?.herFirstName
                                          ? item.herFirstName
                                          : ""
                                      } ${
                                        item?.hisLastName
                                          ? item.hisLastName
                                          : item?.herLastName
                                            ? item.herLastName
                                            : ""
                                      }`}
                                    </h2>
                                    {(item?.hisMobile || item?.hisEmail) && (
                                      <>
                                        <h4>
                                          His:{" "}
                                          {item.hisMobile ? item.hisMobile : ""}
                                        </h4>
                                        {item.hisEmail && (
                                          <h4>{item.hisEmail}</h4>
                                        )}
                                      </>
                                    )}
                                    {(item?.herMobile || item?.herEmail) && (
                                      <>
                                        <h4>
                                          Hers:{" "}
                                          {item.herMobile ? item.herMobile : ""}
                                        </h4>
                                        {item.herEmail && (
                                          <h4>{item.herEmail}</h4>
                                        )}
                                      </>
                                    )}
                                    {item && item.anniversary_date ? (
                                      <h4>
                                        {"Anniversary Date : " +
                                          `${
                                            item && item.anniversary_date
                                              ? Helpers.convertUtcToTimezone(
                                                  item.anniversary_date,
                                                  "MM/DD",
                                                )
                                              : ""
                                          }`}
                                      </h4>
                                    ) : (
                                      ""
                                    )}
                                  </Col>
                                </Row>
                              );
                            })}
                        </Col>
                      ) : null}
                      <Col
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        className="colorTalks mt-3 leaderShip"
                      >
                        {directory &&
                        directory.footerImages &&
                        directory.footerImages.length > 0 ? (
                          <div>
                            {directory.footerImages.map(
                              (footerImg, imgIndex) => (
                                <div
                                  key={imgIndex}
                                  className="text-center footer_images"
                                >
                                  <img
                                    src={footerImg}
                                    className="imageHeader"
                                    alt={`Footer ${imgIndex + 1}`}
                                  />
                                </div>
                              ),
                            )}
                            <div className="retdet1 text-center">
                              <div>
                                {" "}
                                <h2>
                                  {data?.myparish?.parish} Lovestrong Retreat
                                </h2>
                                <h1>
                                  {data.dateFrom && data.dateTo ? (
                                    <span>
                                      {moment(data.dateFrom).isSame(
                                        data.dateTo,
                                        "day",
                                      )
                                        ? // Same day
                                          `${moment(data.dateFrom)
                                            .tz("America/New_York")
                                            .format("MMM D, YYYY")}`
                                        : moment(data.dateFrom).isSame(
                                              data.dateTo,
                                              "month",
                                            )
                                          ? // Same month, show month only once
                                            `${moment(data.dateFrom)
                                              .tz("America/New_York")
                                              .format("MMM D")} - ${moment(
                                              data.dateTo,
                                            )
                                              .tz("America/New_York")
                                              .format("D")}, ${moment(
                                              data.dateFrom,
                                            )
                                              .tz("America/New_York")
                                              .format("YYYY")}`
                                          : // Different months
                                            `${moment(data.dateFrom)
                                              .tz("America/New_York")
                                              .format("MMM D")} - ${moment(
                                              data.dateTo,
                                            )
                                              .tz("America/New_York")
                                              .format("MMM D")}, ${moment(
                                              data.dateFrom,
                                            )
                                              .tz("America/New_York")
                                              .format("YYYY")}`}
                                    </span>
                                  ) : null}
                                </h1>
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <dir className="howerOption mt-5">
                          <h2 className="mb-4">
                            {directory && directory.footerText ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: directory && directory.footerText,
                                }}
                              />
                            ) : (
                              ""
                            )}
                          </h2>
                        </dir>
                        {directory?.socialLinks ? (
                          <div className="social-icons">
                            {directory?.socialLinks?.facebook ? (
                              <a
                                href={directory?.socialLinks?.facebook}
                                target="_blank"
                              >
                                <i className="mdi mdi-facebook"></i>
                              </a>
                            ) : null}
                            {directory?.socialLinks?.twitter ? (
                              <a
                                href={directory?.socialLinks?.twitter}
                                target="_blank"
                              >
                                <i className="mdi mdi-twitter"></i>
                              </a>
                            ) : null}
                            {directory?.socialLinks?.pinterest ? (
                              <a
                                href={directory?.socialLinks?.pinterest}
                                target="_blank"
                              >
                                <i className="mdi mdi-pinterest"></i>
                              </a>
                            ) : null}
                            {directory?.socialLinks?.instagram ? (
                              <a
                                href={directory?.socialLinks?.instagram}
                                target="_blank"
                              >
                                <i className="mdi mdi-instagram"></i>
                              </a>
                            ) : null}
                          </div>
                        ) : null}
                        <div className="text-center">
                          {" "}
                          <img
                            src={lovestrongLogo}
                            alt=""
                            srcset=""
                            className="logo_image"
                          />
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <Row>
                      <Col
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        className="directoryHeader hoverOption"
                      >
                        <div>
                          <h1>
                            <em>
                              <span>No Retreat Found</span>
                            </em>
                          </h1>
                        </div>
                      </Col>
                    </Row>
                  )}
                </div>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Directory;
